import {
  Alert,
  Button,
  ColumnLayout,
  Container,
  Form,
  FormField,
  Header,
  Input,
  RadioGroup,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { createZipBlast } from '../../../common/api/zipBlasts';
import { DashboardViewLayout } from '../../../components/DashboardViewLayout';
import {
  buildZipBlastCreateBody,
  validateZipBlastForm,
  type ZipBlastFormState,
  type ZipBlastMode,
} from './validation';

const INITIAL_FORM: ZipBlastFormState = {
  mode: 'single_zip',
  zip: '',
  city: '',
  state: '',
  zipCodes: '',
  dailyCap: '',
  notes: '',
};

export default function NewZipBlastPage() {
  const navigate = useNavigate();
  const [formState, setFormState] = useState<ZipBlastFormState>(INITIAL_FORM);
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const errors = useMemo(() => validateZipBlastForm(formState), [formState]);
  const canSubmit = Object.keys(errors).length === 0 && !submitting;

  function updateForm(patch: Partial<ZipBlastFormState>) {
    setFormState((current) => ({ ...current, ...patch }));
  }

  function visibleError(key: keyof typeof errors) {
    return touched ? errors[key] : undefined;
  }

  async function submit() {
    setTouched(true);
    if (Object.keys(errors).length > 0) return;

    setErrorMsg(null);
    setSubmitting(true);
    try {
      const created = await createZipBlast(buildZipBlastCreateBody(formState));
      if (!created) {
        setErrorMsg('Server returned no body');
        return;
      }
      navigate({ to: '/dashboard/zip-blasts/$uuid', params: { uuid: created.uuid } });
    } catch (e) {
      setErrorMsg(String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <DashboardViewLayout
      breadcrumbs={[
        { text: 'Zip Blasts', href: '/dashboard/zip-blasts' },
        { text: 'New', href: '/dashboard/zip-blasts/new' },
      ]}
      header={
        <Header
          variant="h1"
          description="Start a restaurant scorecard blast from one ZIP, a City / area, or an explicit ZIP-list. The existing single-ZIP flow remains available."
        >
          New zip blast
        </Header>
      }
    >
      <Container>
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => navigate({ to: '/dashboard/zip-blasts' })}>
                Cancel
              </Button>
              <Button variant="primary" loading={submitting} disabled={!canSubmit} onClick={submit}>
                Submit
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="l">
            {errorMsg ? (
              <Alert type="error" header="Submit failed">
                {errorMsg}
              </Alert>
            ) : null}

            <FormField
              label="Blast mode"
              description="Choose how the backend should scope discovery."
            >
              <RadioGroup
                value={formState.mode}
                onChange={({ detail }) => {
                  updateForm({ mode: detail.value as ZipBlastMode });
                  setTouched(false);
                  setErrorMsg(null);
                }}
                items={[
                  {
                    value: 'single_zip',
                    label: 'Single ZIP',
                    description: 'Existing single-zip behavior and Zip code payload.',
                    disabled: submitting,
                  },
                  {
                    value: 'city_area',
                    label: 'City / area',
                    description: 'Server expands City and State to child ZIPs for an area blast.',
                    disabled: submitting,
                  },
                  {
                    value: 'zip_list',
                    label: 'Explicit ZIP list',
                    description: 'Paste a comma, space, or line-separated ZIP codes list.',
                    disabled: submitting,
                  },
                ]}
              />
            </FormField>

            {formState.mode === 'single_zip' ? (
              <FormField
                label="Zip code"
                description="5-digit US zip — restaurants are matched via OpenTable + Google Maps."
                errorText={visibleError('zip')}
              >
                <Input
                  value={formState.zip}
                  onChange={(e) => updateForm({ zip: e.detail.value.trim() })}
                  onBlur={() => setTouched(true)}
                  placeholder="75201"
                  inputMode="numeric"
                  disabled={submitting}
                />
              </FormField>
            ) : null}

            {formState.mode === 'city_area' ? (
              <ColumnLayout columns={2} variant="text-grid">
                <FormField
                  label="City"
                  description="Example: Dallas"
                  errorText={visibleError('city')}
                >
                  <Input
                    value={formState.city}
                    onChange={(e) => updateForm({ city: e.detail.value })}
                    onBlur={() => setTouched(true)}
                    placeholder="Dallas"
                    disabled={submitting}
                  />
                </FormField>
                <FormField
                  label="State"
                  description="2-letter state code"
                  errorText={visibleError('state')}
                >
                  <Input
                    value={formState.state}
                    onChange={(e) =>
                      updateForm({ state: e.detail.value.toUpperCase().slice(0, 2) })
                    }
                    onBlur={() => setTouched(true)}
                    placeholder="TX"
                    disabled={submitting}
                  />
                </FormField>
              </ColumnLayout>
            ) : null}

            {formState.mode === 'zip_list' ? (
              <FormField
                label="ZIP codes"
                description="Comma, space, or line-separated 5-digit ZIP codes. Duplicates are removed before submit."
                errorText={visibleError('zipCodes')}
              >
                <Textarea
                  value={formState.zipCodes}
                  onChange={(e) => updateForm({ zipCodes: e.detail.value })}
                  onBlur={() => setTouched(true)}
                  rows={4}
                  placeholder="75201, 75204, 75206"
                  disabled={submitting}
                />
              </FormField>
            ) : null}

            <FormField
              label="Daily cap"
              description="Optional maximum emails to schedule per day for this blast; leave blank to use the backend default."
              errorText={visibleError('dailyCap')}
            >
              <Input
                value={formState.dailyCap}
                onChange={(e) => updateForm({ dailyCap: e.detail.value.trim() })}
                onBlur={() => setTouched(true)}
                placeholder="25"
                inputMode="numeric"
                disabled={submitting}
              />
            </FormField>

            <FormField
              label="Notes (optional)"
              description="Free-form context — useful if you're warming up a new sender or testing a template variant."
            >
              <Textarea
                value={formState.notes}
                onChange={(e) => updateForm({ notes: e.detail.value })}
                rows={3}
                placeholder="e.g. 'Dallas area warmup batch — OpenTable campaign.'"
                disabled={submitting}
              />
            </FormField>
          </SpaceBetween>
        </Form>
      </Container>
    </DashboardViewLayout>
  );
}
